import { LinearProgress, Typography } from "@mui/material";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BsCalendar2Date } from "react-icons/bs";
import { useParams, useSearchParams } from "react-router-dom";
import FullScreenMessageCore from "../../../../components/fullScreenMessage/FullScreenMessageCore";
import BookingModal from "../../../../components/modals/bookingModalComponents/components/BookingModal";
import BookingModalContext from "../../../../components/modals/bookingModalComponents/context/BookingModalContext";
import CustomDialog from "../../../../components/wrappers/CustomDrawer";
import NoClinicWrapper from "../../../../components/wrappers/NoClinicWrapper";
import ToggleDrawer from "../../../../contexts/ToggleDrawer";
import {
  PET_INFINITE_APPOINTMENTS,
  USER_INFINITE_APPOINTMENTS,
} from "../../../../enums/ApiPaths";
import { STATUSES } from "../../../../enums/AppointmentStatus";
import { APPOINTMENTS_INFINITE } from "../../../../enums/QueryKeys";
import { useGetInfiniteAppointments } from "../../../../hooks/useGetAppointments";
import usePrimaryColor from "../../../../hooks/usePrimaryColor";
import { futurePastFilterKeys } from "../../enums/UserAppointmentFuturePastFilterKeys";
import { AppointmentMapperInfinite } from "./components/AppointmentMapper";
import AppointmentUserFilters from "./components/AppointmentUserFilters";
const timeFrame = "future";

function useActiveFilters(searchParams) {
  return useMemo(() => {
    const filterKeys = ["service", "pet", "from", "to", "status"];
    return filterKeys.some((key) => searchParams.get(key));
  }, [searchParams]);
}

export default function Future({ isOwnerDeleted = false }) {
  const [searchParams] = useSearchParams();
  const hasActiveFilters = useActiveFilters(searchParams);

  const filterParams = futurePastFilterKeys.reduce((acc, key) => {
    const value = searchParams.get(key);
    if (value && value !== "null") {
      acc[key] = value;
    }
    return acc;
  }, {});

  const queryKey = [
    APPOINTMENTS_INFINITE,
    timeFrame,
    USER_INFINITE_APPOINTMENTS,
    filterParams,
  ];
  const {
    data,
    isLoading,
    fetchNextPage,
    isFetchNextPageError,
    isFetchingNextPage,
    hasNextPage,
  } = useGetInfiniteAppointments(
    timeFrame,
    USER_INFINITE_APPOINTMENTS,
    queryKey,
    filterParams,
  );

  const futureStatusTitle = [
    { title: STATUSES.ACTIVE, value: STATUSES.ACTIVE },
    { title: STATUSES.WAITING_CLINIC, value: STATUSES.WAITING_CLINIC },
  ];

  return (
    <Fragment>
      <AppointmentUserFilters
        statusOptions={futureStatusTitle}
        timeframe={timeFrame}
      />
      <FutureContent
        data={data}
        hasNextPage={hasNextPage}
        isLoading={isLoading}
        fetchNextPage={fetchNextPage}
        isFetchNextPageError={isFetchNextPageError}
        isFetchingNextPage={isFetchingNextPage}
        hasActiveFilters={hasActiveFilters}
        isDeceased={data?.deceased || false}
        isOwnerDeleted={isOwnerDeleted}
      />
    </Fragment>
  );
}

export function FuturePet({ isDeceased = false, isOwnerDeleted = false }) {
  const { id: petId } = useParams();
  const queryKey = [
    APPOINTMENTS_INFINITE,
    timeFrame,
    PET_INFINITE_APPOINTMENTS + "/" + petId,
  ];
  const {
    data,
    isLoading,
    fetchNextPage,
    isFetchNextPageError,
    isFetchingNextPage,
    hasNextPage,
  } = useGetInfiniteAppointments(
    timeFrame,
    PET_INFINITE_APPOINTMENTS + "/" + petId,
    queryKey,
  );

  return (
    <FutureContent
      data={data}
      hasNextPage={hasNextPage}
      isLoading={isLoading}
      fetchNextPage={fetchNextPage}
      isFetchNextPageError={isFetchNextPageError}
      isFetchingNextPage={isFetchingNextPage}
      isDeceased={isDeceased}
      isOwnerDeleted={isOwnerDeleted}
    />
  );
}

function FutureContent({
  data,
  isFetchNextPageError,
  isFetchingNextPage,
  fetchNextPage,
  isLoading,
  hasNextPage,
  hasActiveFilters = false,
  isDeceased = false,
  isOwnerDeleted = false,
}) {
  if (isLoading) {
    return <LinearProgress />;
  }
  const pages = data?.pages ?? [];
  const content = pages?.flatMap((page) => page?.content) ?? [];

  if (content?.length == 0) {
    return (
      <BookingDialog
        hasActiveFilters={hasActiveFilters}
        isDeceased={isDeceased}
        isOwnerDeleted={isOwnerDeleted}
      />
    );
  }
  const title = hasActiveFilters
    ? "NoFutureAppointmentsWithFilters"
    : "NoFutureAppointments";

  return (
    <AppointmentMapperInfinite
      hasNextPage={hasNextPage}
      isFetchNextPageError={isFetchNextPageError}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      appointments={content}
      title={title}
      isOwnerDeleted={isOwnerDeleted}
      containerHeight="66vh"
    />
  );
}

function BookingDialog({
  hasActiveFilters = false,
  isDeceased = false,
  isOwnerDeleted = false,
}) {
  const color = usePrimaryColor();
  const { t } = useTranslation();
  const title = hasActiveFilters
    ? "NoFutureAppointmentsWithFilters"
    : "NoFutureAppointments";
  const isPetDeceased = typeof isDeceased === "boolean" ? isDeceased : false;
  const isBookingBlocked = isPetDeceased || isOwnerDeleted;
  return (
    <FullScreenMessageCore
      withoutWrapper
      icon={<BsCalendar2Date size={150} color={color} />}
      title={title}
    >
      {isBookingBlocked ? (
        <Typography data-testid={"appointment.user-tabs.booking-blocked-message"}>
          {isPetDeceased
            ? t("PetDeceasedBookingNotAvailable")
            : t("OwnerDeletedBookingNotAvailable")}
        </Typography>
      ) : (
        <NoClinicWrapper>
          <ToggleDrawer>
            <CustomDialog
              buttonType={"contained"}
              sx={{ bgcolor: "white" }}
              buttonMessage={t("Book")}
              buttonSize={"large"}
              maxWidth={"lg"}
              testId={"appointment.user-tabs.book-button"}
            >
              <BookingModalContext key={"service-booking-context"}>
                <BookingModal key={"service-booking-modal"} />
              </BookingModalContext>
            </CustomDialog>
          </ToggleDrawer>
        </NoClinicWrapper>
      )}
    </FullScreenMessageCore>
  );
}
